import { memo, useState, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { ChatMessage } from '../types';
import { useWidgetStore } from '../state/createWidgetStore';
import { webchatApi } from '../services/api';
import { resolveSpeechLocale } from '../utils/locale';
import { getTtsService } from '../services/tts';
import { stripFileReferenceMetadata } from '../utils/file-field';
import { formatTime, selectVisibleToolCalls, HIDDEN_TOOL_CALL_NAMES } from './messageBubble/helpers';
import { DownloadCard } from './messageBubble/DownloadCard';
import { UserAttachmentCard } from './messageBubble/UserAttachmentCard';
import { ToolCallIndicator } from './messageBubble/ToolCallIndicator';
import { ConfirmationCard } from './messageBubble/ConfirmationCard';
import { MessageText } from './messageBubble/MessageText';
import { MessageMeta } from './messageBubble/MessageMeta';
import { TurnErrorActions } from './messageBubble/TurnErrorActions';
import { AnalystProgressLine } from './messageBubble/AnalystProgressLine';

interface MessageBubbleProps {
  msg: ChatMessage;
  portalMode?: boolean;
}

// Matches the fixed prefix buildAiFormMessage() puts on the raw message sent to the
// backend/LLM (e.g. "Form submitted for get_annual_revenues: "). That raw text has to stay
// intact in msg.content - the LLM reads it to build the tool call - so it's reworded here
// for display only, never touching msg.content itself.
const FORM_SUBMITTED_PREFIX = /^Form submitted(?: for [^:]+)?:\s*/;
const FRIENDLY_FORM_SUBMITTED_LEAD: Record<'en' | 'he', string> = {
  en: "We'll send your details:",
  he: 'נשלח את הנתונים שלך:',
};

function MessageBubbleInner({ msg, portalMode = false }: MessageBubbleProps) {
  const isUser = msg.role === 'user';
  const voiceEnabled = useWidgetStore((s) => s.config.voiceEnabled);
  const language = useWidgetStore((s) => s.config.language);
  const conversationId = useWidgetStore((s) => s.conversationId);
  const setMessageFeedback = useWidgetStore((s) => s.setMessageFeedback);
  const toolCalls = useWidgetStore(
    useShallow((s) => selectVisibleToolCalls(s.toolCalls.filter((tc) => tc.messageId === msg.id && !HIDDEN_TOOL_CALL_NAMES.has(tc.name)))),
  );

  const [isPlaying, setIsPlaying] = useState(false);
  const ttsRef = useRef(getTtsService());
  const messageTextRef = useRef<HTMLElement | null>(null);

  // Stop TTS when component unmounts
  useEffect(() => {
    return () => {
      if (isPlaying) ttsRef.current.stop();
    };
  }, [isPlaying]);

  const showFeedback = !isUser && !msg.isStreaming && !!msg.content;
  const timeStr = formatTime(msg.timestamp);

  // User messages: strip [File: ...] display markers
  let displayContent = msg.content;
  if (isUser && msg.attachments?.length) {
    displayContent = displayContent.replace(/\[File: [^\]]+\]\s*/g, '').trim();
  } else if (isUser) {
    // Form/chip submissions echo raw "[File: name (s3_key: ...)]" markers for any file
    // field the attachment-matching heuristics couldn't resolve to a plain value - show
    // just the filename instead of the full metadata dump.
    displayContent = stripFileReferenceMetadata(displayContent);
    if (FORM_SUBMITTED_PREFIX.test(displayContent)) {
      displayContent = displayContent.replace(
        FORM_SUBMITTED_PREFIX,
        `${FRIENDLY_FORM_SUBMITTED_LEAD[language]}\n`,
      );
    }
  }

  const getVisibleMessageText = (): string => {
    if (isUser) return displayContent;
    const textEl = messageTextRef.current;
    if (!textEl) return msg.content;
    return (textEl.innerText || textEl.textContent || '').trim() || msg.content;
  };

  const handleFeedback = (rating: 'positive' | 'negative') => {
    setMessageFeedback(msg.id, rating);
    if (conversationId) {
      webchatApi
        .submitFeedback(conversationId, msg.id, rating)
        .catch(() => { /* best-effort */ });
    }
  };

  const handleTts = () => {
    const tts = ttsRef.current;
    if (isPlaying) {
      tts.stop();
      setIsPlaying(false);
      return;
    }
    const text = getVisibleMessageText();
    const locale = resolveSpeechLocale(text);
    tts.speak(text, locale, {
      onEnd: () => setIsPlaying(false),
      onError: () => setIsPlaying(false),
    });
    setIsPlaying(true);
  };

  return (
    <div
      className={`message ${isUser ? 'message-user' : 'message-assistant'}`}
      data-message-id={msg.id}
    >
      <div className="message-content">
        {isUser && msg.attachments?.map((att) => (
          <UserAttachmentCard key={att.fileId} att={att} />
        ))}

        <MessageText
          isUser={isUser}
          displayContent={displayContent}
          content={msg.content}
          isStreaming={msg.isStreaming}
          onRef={(el) => { messageTextRef.current = el; }}
        />

        {/* Always mounted so it keeps its place above the tool cards, which tool-indicator-dom appends imperatively */}
        {!isUser && (
          <div className="analyst-progress-slot">
            {msg.isStreaming && msg.analystProgress && <AnalystProgressLine progress={msg.analystProgress} />}
          </div>
        )}

        {toolCalls.map((tc) => (
          <ToolCallIndicator key={tc.id} tc={tc} />
        ))}

        <ConfirmationCard messageId={msg.id} />

        {msg.files?.map((file, i) => (
          <DownloadCard key={i} file={file} />
        ))}

        {portalMode && msg.turnError?.turnId && (
          <TurnErrorActions messageId={msg.id} turnId={msg.turnError.turnId} />
        )}
      </div>

      <MessageMeta
        showFeedback={showFeedback}
        voiceEnabled={voiceEnabled}
        isPlaying={isPlaying}
        feedback={msg.feedback ?? undefined}
        timeStr={timeStr}
        iterationTokens={msg.iterationTokens}
        summarizationEvents={msg.summarizationEvents}
        onFeedback={handleFeedback}
        onTts={handleTts}
      />
    </div>
  );
}

export const MessageBubble = memo(MessageBubbleInner, (prevProps, nextProps) => {
  const prev = prevProps.msg;
  const next = nextProps.msg;
  if (prev === next) return true;
  return (
    prev.id === next.id &&
    prev.role === next.role &&
    prev.content === next.content &&
    prev.isStreaming === next.isStreaming &&
    prev.analystProgress === next.analystProgress &&
    prev.feedback === next.feedback &&
    prev.awaitingInput === next.awaitingInput &&
    prev.turnError === next.turnError &&
    prev.attachments === next.attachments &&
    prev.files === next.files &&
    prev.timestamp === next.timestamp
  );
});
